// ── Data Model ──────────────────────────────────────────────────────────────
//
// What the tables are and how they relate, generated from the database itself
// rather than from a diagram somebody has to remember to update. Every box and
// every line here comes from sys.tables, sys.columns, sys.foreign_keys and
// sys.sql_expression_dependencies at the moment the page loads, so it cannot
// quietly stop being true.
//
// ── WHY NOT ONE BIG CANVAS ──────────────────────────────────────────────────
//
// The obvious build is the Power BI model view: every table on a pannable
// surface with lines between them. At this schema's size that produces a
// hairball — a picture that looks like an answer and teaches nothing, where the
// only way to follow a relationship is to trace a line across three screens.
//
// So the page is a browser, not a poster. Clusters navigate, and the diagram
// shows ONE table and its immediate neighbours at a time. Clicking a neighbour
// re-centres on it, which means you walk the schema the way you'd actually
// reason about it — from the thing you care about outwards — instead of
// scanning for your table in a field of boxes.
//
// ── THE LINES ARE NOT ALL THE SAME KIND OF FACT ─────────────────────────────
//
// Most relationships in this database are not enforced. BankAccounts.accountid
// points at Accounts.id because the code says so, not because the database
// insists. Drawing those identically to real foreign keys would be a lie that
// flatters the schema, so each edge carries its provenance and the header says
// plainly how many of them the database actually guarantees. That number is the
// most useful thing on the page for anyone deciding how much to trust a join.
import { useState, useEffect, useMemo, useCallback } from "react";
import { B, INK_MUTED, INK_PRIMARY, HAIRLINE } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, Btn, SearchBar, SectionHeader, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const num = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());

// How an edge got here, and how much it is worth. Order is the legend order.
const VIA = {
  fk:         { label: "Foreign key", color: "#0F6E56", note: "enforced by the database" },
  declared:   { label: "Declared",    color: "#8A6100", note: "asserted in getDataModel, nothing enforces it" },
  convention: { label: "Convention",  color: "#6B7280", note: "inferred from the column name, nothing enforces it" },
  view:       { label: "View reads",  color: "#4A4192", note: "this view selects from that table" },
};

// Subject areas, most specific first. These are naming rules, not a taxonomy
// somebody maintains by hand: a table that does not match any of them lands in
// "Other", which is a visible prompt to either rename it or add a rule rather
// than a quiet default.
const AREAS = [
  [/^customerBank/i,                          "Banking — Plaid feed"],
  [/^Bank(AccountLink|Import|Migration)/i,    "Banking — mapping & import"],
  [/^(BankAccounts|Bank_|wftransactions|ACH)/i, "Banking — accounts & payments"],
  [/^(Loans|Terms|Draw|Facility)/i,           "Loans & terms"],
  [/^(Collateral|AR[A-Z_]|Inventory|BBC)/i,   "Collateral"],
  [/^Covenant/i,                              "Covenants"],
  [/^(Statement|Invoice|Payment|Pmt)/i,       "Statements & invoicing"],
  [/^(Accounts|Borrower|Contact|SF_|Industry)/i, "Borrowers & accounts"],
  [/^(Sales|Email|Activity|Lead|Deal)/i,      "Sales & pipeline"],
  [/^(User|Audit|Role|Tenant|Org|Setting|Log)/i, "Administration"],
  [/^(Fund|Participant|Investor)/i,           "Funds & participations"],
];

// A view is named for its subject with a v in front; strip it before matching
// so vBankTransactionDetail files with the banking tables rather than in a
// bucket of its own. Where a view lives matters more than that it is a view.
const areaOf = (n) => {
  const base = n.kind === "view" ? n.name.replace(/^v(?=[A-Z])/, "") : n.name;
  for (const [re, label] of AREAS) if (re.test(base)) return label;
  return "Other";
};

// ── an editable description ─────────────────────────────────────────────────
//
// The text is SQL Server's own MS_Description extended property — the same
// field SSMS shows in a column's properties — so this is a second window onto
// one value rather than a copy of it. Nothing is stored in an ACE table.
//
// ── One description, staged rather than saved ───────────────────────────────
//
// Reads as plain text until clicked, because most of the time this page is
// being read rather than written and a grid of input boxes is harder to scan
// than a grid of sentences.
//
// IT NO LONGER SAVES ITSELF. It used to own its own draft text and its own
// Save button, which meant saving one description re-read the whole model and
// every other box you had open reverted to plain text — losing whatever was
// typed in it. Opening five and saving one destroyed the other four silently.
//
// So the text lives on the page instead, in one map, and the card has one
// Save. This component only says "I am open" and "here is what is in me".
const Description = ({ value, status, placeholder, canEdit,
                      open, staged, onOpen, onChange, onClose, onRevert }) => {
  const edited = staged !== undefined && staged !== (value || "");

  if (!open) {
    return (
      <div onClick={canEdit ? onOpen : undefined}
           title={canEdit ? "Click to edit" : undefined}
           style={{ fontSize: 12, lineHeight: 1.5, cursor: canEdit ? "text" : "default",
                    color: (staged ?? value) ? INK_PRIMARY : INK_MUTED,
                    fontStyle: (staged ?? value) ? "normal" : "italic" }}>
        {/* An edit that is typed but not yet committed has to look different
            from one that is in the database, or the page lies about what is
            stored the moment you close a box. */}
        {edited && (
          <><Chip color="#8A6100" title="Edited but not saved yet. Use Save on the card above.">
            unsaved
          </Chip>{" "}</>
        )}
        {!edited && value && status === "draft" && (
          <><Chip color="#8A6100" title="Backfilled and not yet checked by a person. Saving it — even unchanged — confirms it.">
            draft
          </Chip>{" "}</>
        )}
        {(staged ?? value) || placeholder}
      </div>
    );
  }

  return (
    <div>
      <textarea value={staged ?? (value || "")} autoFocus rows={3} maxLength={3900}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
                style={{ width: "100%", boxSizing: "border-box", fontSize: 12,
                         lineHeight: 1.5, padding: 6, borderRadius: 4,
                         border: "1px solid " + (status === "draft" ? "#D9A441" : HAIRLINE),
                         fontFamily: "inherit", resize: "vertical" }} />
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
        {/* Closes the box and KEEPS what was typed. Nothing here writes to the
            database — that is the card's Save, once, for all of them. */}
        <Btn onClick={onClose}>Done</Btn>
        {edited && <Btn onClick={onRevert}>Revert</Btn>}
        <span style={{ fontSize: 11, color: INK_MUTED }}>
          {/* Not a style limit. The property is stored as sql_variant, which
              rejects anything longer outright. */}
          {(staged ?? (value || "")).length}/3900 · empty clears it · Esc closes
          {status === "draft" ? " · saving confirms this draft" : ""}
        </span>
      </div>
    </div>
  );
};

const Chip = ({ children, color, title }) => (
  <span title={title} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999,
    fontWeight: 600, background: (color || INK_MUTED) + "22", color: color || INK_MUTED }}>
    {children}
  </span>
);

// ── the neighbourhood ───────────────────────────────────────────────────────
//
// Selected table in the middle, everything one hop away around it. Laid out on
// a circle rather than by a force simulation: this is a reference picture
// people come back to, and it should look the same every time they open it.
const Neighbourhood = ({ centre, edges, onPick }) => {
  // ── Sized as an ILLUSTRATION, not a schema poster ─────────────────────────
  // It draws about half the space it used to. Two changes do that, and they
  // have to move together:
  //
  //   the SVG renders at ~480px instead of the full card width, and
  //   the type inside it is set LARGER in viewBox units so it survives the
  //   shrink — 17 units at 0.69 scale lands at ~11.7px on screen, which is
  //   bigger than the 11px it was at full width.
  //
  // Halving the render without raising the type would have produced 6px
  // labels: a picture of table names rather than table names.
  //
  // FEWER NEIGHBOURS DRAWN (12, was 18) because the boxes grew with the type.
  // At 18 they overlapped badly even before this; the ones not drawn are named
  // in the note underneath and every one of them is in the table below, so
  // nothing is lost but clutter.
  const W = 700, H = 400, CX = W / 2, CY = H / 2, R = 168;

  const around = useMemo(() => {
    const seen = new Map();
    for (const e of edges) {
      const other = e.from === centre ? e.to : e.from === undefined ? null : e.from;
      const name  = e.from === centre ? e.to : e.from;
      if (name === centre) continue;
      const dir = e.from === centre ? "out" : "in";
      const prev = seen.get(name);
      // One node per neighbour even when several columns connect them; the
      // strongest provenance wins the line colour, and the relationships table
      // below carries every individual column.
      const rank = { fk: 3, declared: 2, view: 1, convention: 0 };
      if (!prev || rank[e.via] > rank[prev.via]) {
        seen.set(name, { name, via: e.via, dir, col: e.fromCol });
      }
      void other;
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [centre, edges]);

  const shown = around.slice(0, 12);
  const hidden = around.length - shown.length;

  if (!shown.length) {
    return <div style={{ padding: 40, textAlign: "center", fontSize: 13, color: INK_MUTED }}>
      Nothing connects to <strong>{centre}</strong> — no foreign key, no column
      that names another table, and no view that reads it.
    </div>;
  }

  const pos = shown.map((n, i) => {
    const a = (i / shown.length) * Math.PI * 2 - Math.PI / 2;
    return { ...n, x: CX + Math.cos(a) * R, y: CY + Math.sin(a) * R };
  });

  const box = (name) => Math.max(98, Math.min(210, name.length * 9.5 + 20));

  return <>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%"
         style={{ display: "block", overflow: "visible",
                  /* The halving. width:100% with a cap rather than a fixed
                     width, so it still shrinks on a narrow window instead of
                     forcing a horizontal scrollbar. */
                  maxWidth: 480, margin: "0 auto" }}>
      {pos.map((n) => (
        <line key={"l" + n.name} x1={CX} y1={CY} x2={n.x} y2={n.y}
              stroke={VIA[n.via].color} strokeWidth={n.via === "fk" ? 2 : 1}
              strokeDasharray={n.via === "convention" ? "3 3" : undefined}
              opacity={0.55} />
      ))}

      {pos.map((n) => {
        const w = box(n.name);
        return (
          <g key={n.name} onClick={() => onPick(n.name)} style={{ cursor: "pointer" }}>
            <rect x={n.x - w / 2} y={n.y - 16} width={w} height={32} rx={6}
                  fill="var(--color-surface, #fff)" stroke={VIA[n.via].color} strokeWidth="1" />
            <text x={n.x} y={n.y + 5} textAnchor="middle"
                  style={{ fontSize: 17, fill: INK_PRIMARY }}>{n.name}</text>
            <title>{`${n.dir === "out" ? centre + " → " + n.name : n.name + " → " + centre}`
              + `${n.col ? " (" + n.col + ")" : ""}\n${VIA[n.via].label}: ${VIA[n.via].note}`}</title>
          </g>
        );
      })}

      <rect x={CX - box(centre) / 2 - 6} y={CY - 20} width={box(centre) + 12} height={40} rx={8}
            fill={B.cyan} opacity={0.12} stroke={B.cyanDark} strokeWidth="1.5" />
      <text x={CX} y={CY + 6} textAnchor="middle"
            style={{ fontSize: 19, fontWeight: 700, fill: INK_PRIMARY }}>{centre}</text>
    </svg>
    {hidden > 0 && (
      <div style={{ fontSize: 11, color: INK_MUTED, textAlign: "center", marginTop: 4 }}>
        {hidden} more neighbour{hidden === 1 ? "" : "s"} not drawn — all of them are in the table below.
      </div>
    )}
  </>;
};

export const DataModelPage = () => {
  const { api, role } = useApp();

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr]     = useState(null);
  const [q, setQ]         = useState("");
  const [sel, setSel]     = useState(null);
  const [open, setOpen]   = useState({});      // area -> collapsed?

  const load = useCallback(() => {
    setLoad(true); setErr(null);
    api.get("getDataModel")
      .then((d) => { setData(d); setLoad(false); })
      .catch((e) => { setErr(e.message); setLoad(false); });
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Saves the property, then re-reads rather than patching state locally. The
  // round trip is cheap and it means what the page shows is what the database
  // holds — including a description the procedure cleared because it was blank.
  // The batch form of the same call. One round trip for a whole table, which
  // matters because the thing people actually do here is confirm forty drafts
  // in one sitting — and forty round trips is forty chances to be interrupted
  // half way and not know where you got to.
  const saveDescBulk = useCallback(async (items) => {
    if (!items.length) return { written: 0, failed: 0 };
    const r = await api.post("saveColumnDescription", { items });
    load();
    return r || { written: items.length, failed: 0 };
  }, [api, load]);

  // ONE staging area for the whole card. Every open box writes into this and
  // nothing writes to the database until Save. That is the fix for the thing
  // that made this annoying: saving one description used to re-read the model
  // and blank every other box that was open.
  //
  // Keyed by column name; the table's own description uses TKEY, which cannot
  // collide because no column is named with a NUL byte.
  const TKEY = "\u0000table";
  const [edits, setEdits] = useState({});          // key -> staged text
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg]   = useState(null);

  const openKey  = (k) => setOpenKeys(cur => new Set(cur).add(k));
  const closeKey = (k) => setOpenKeys(cur => { const n = new Set(cur); n.delete(k); return n; });
  const stage    = (k, text) => setEdits(e => ({ ...e, [k]: text }));
  const revert   = (k) => setEdits(e => { const n = { ...e }; delete n[k]; return n; });

  const nodes = (data && data.nodes) || [];
  const edges = (data && data.edges) || [];
  const counts = (data && data.counts) || { byVia: {} };

  const byName = useMemo(() => new Map(nodes.map((n) => [n.name, n])), [nodes]);

  const areas = useMemo(() => {
    const m = new Map();
    const needle = q.trim().toLowerCase();
    // Search reaches into column names AND descriptions, not just table names.
    // That is what turns this from a schema listing into documentation you can
    // actually look something up in — "which column holds the pending link" is
    // the question people have, and they rarely know the table.
    const hit = (n) => !needle
      || n.name.toLowerCase().includes(needle)
      || (n.description || "").toLowerCase().includes(needle)
      || n.columns.some((c) => c.name.toLowerCase().includes(needle)
                            || (c.description || "").toLowerCase().includes(needle));
    for (const n of nodes) {
      if (!hit(n)) continue;
      const a = areaOf(n);
      if (!m.has(a)) m.set(a, []);
      m.get(a).push(n);
    }
    for (const list of m.values()) list.sort((x, y) => x.name.localeCompare(y.name));
    // "Other" last; everything else by size, so the areas that carry the
    // platform come first rather than alphabetically.
    return [...m.entries()].sort((a, b) =>
      a[0] === "Other" ? 1 : b[0] === "Other" ? -1 : b[1].length - a[1].length);
  }, [nodes, q]);

  // Enforcement per area, because the gap is not spread evenly and an overall
  // ratio hides that. The original loan schema is well constrained; the banking
  // and Plaid tables added later are not. Showing it per area turns a statistic
  // into a list of the places worth fixing.
  const enforcementByArea = useMemo(() => {
    const m = new Map();
    const areaByName = new Map(nodes.map((n) => [n.name, areaOf(n)]));
    for (const e of edges) {
      if (e.via === "view") continue;            // not an integrity claim
      const a = areaByName.get(e.from);
      if (!a) continue;
      const cur = m.get(a) || { fk: 0, total: 0 };
      cur.total += 1;
      if (e.via === "fk") cur.fk += 1;
      m.set(a, cur);
    }
    return m;
  }, [nodes, edges]);

  const selEdges = useMemo(
    () => edges.filter((e) => e.from === sel || e.to === sel), [edges, sel]);
  const selNode = sel ? byName.get(sel) : null;

  // Leaving a table closes bulk editing and throws away anything typed but not
  // saved. Carrying it across tables would silently apply one table's edits to
  // another's column names, which is the worst kind of bug here — it would
  // succeed on the names that happen to collide.
  useEffect(() => { setEdits({}); setOpenKeys(new Set()); setBulkMsg(null); }, [sel]);

  // Leaving the page or the browser tab with edits still staged loses them, so
  // say so. The in-app table switch clears them above; this covers the ways
  // out that React cannot intercept.
  useEffect(() => {
    if (!Object.keys(edits).length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [edits]);

  const draftCols = selNode ? selNode.columns.filter(c => c.status === "draft" && c.description) : [];

  // Only what actually differs from what is stored. A box that was opened,
  // looked at and closed unchanged is not a change, and sending it would mark
  // a draft reviewed that nobody actually reviewed.
  const pending = !selNode ? [] : [
    ...(edits[TKEY] !== undefined && edits[TKEY] !== (selNode.description || "")
        ? [{ table: selNode.name, column: null, description: edits[TKEY], label: selNode.name + " (table)" }]
        : []),
    ...selNode.columns
      .filter(c => edits[c.name] !== undefined && edits[c.name] !== (c.description || ""))
      .map(c => ({ table: selNode.name, column: c.name, description: edits[c.name], label: c.name })),
  ];

  const runBulk = async (items, what) => {
    setBulkBusy(true); setBulkMsg(null);
    try {
      const r = await saveDescBulk(items);
      setBulkMsg(r.failed
        ? { bad: true, text: `${r.written} saved, ${r.failed} failed — ${(r.results || []).filter(x => !x.ok).map(x => x.column || "(table)").join(", ")}` }
        : { bad: false, text: `${r.written} ${what}.` });
      setEdits({}); setOpenKeys(new Set());
    } catch (e) {
      setBulkMsg({ bad: true, text: e.message });
    } finally { setBulkBusy(false); }
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const totalEdges = edges.length;
  const enforced = counts.foreignKeys || 0;

  return <>
    <SectionHeader title="Data Model" actions={[
      <span key="n" style={{ fontSize: 12, color: INK_MUTED, alignSelf: "center" }}>
        {num(counts.tables)} tables · {num(counts.views)} views · {num(totalEdges)} relationships
      </span>,
      <Btn key="r" onClick={load}>Reload</Btn>,
    ]} />

    {/* The headline number, stated rather than buried. How much of this
        structure the database itself guarantees is the first thing anyone
        joining these tables needs to know. */}
    <Card style={{ padding: "12px 14px", marginBottom: 12,
                   borderLeft: "3px solid " + (enforced === 0 ? "#8A6100" : B.cyanDark) }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        {enforced === 0
          ? <><strong>None of these relationships are enforced by the database.</strong>{" "}
              There are no foreign keys. Every line below is held by application code,
              which means a join can silently return nothing when an id stops matching.</>
          : <><strong>{num(enforced)} of {num(totalEdges)}</strong> relationships are
              enforced by a foreign key. The rest are held by application code.</>}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {Object.entries(VIA).map(([k, v]) => (
          <span key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
            <span style={{ width: 16, height: 2, background: v.color, display: "inline-block",
                           opacity: k === "convention" ? 0.6 : 1 }} />
            <span style={{ color: INK_PRIMARY }}>{v.label}</span>
            <span style={{ color: INK_MUTED }}>{num(counts.byVia[k] || 0)}</span>
          </span>
        ))}
      </div>
    </Card>

    <Card style={{ padding: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <SearchBar value={q} onChange={setQ}
                   placeholder="Table, column, or anything in a description…" />
        {(() => {
          const cols = nodes.reduce((a, n) => a + n.columns.length, 0);
          const done = nodes.reduce((a, n) => a + n.described, 0);
          const dft  = nodes.reduce((a, n) => a + (n.drafts || 0), 0);
          return (
            <span style={{ fontSize: 12, color: INK_MUTED }}>
              {num(done)} of {num(cols)} columns documented
              {cols ? ` · ${Math.round((done / cols) * 100)}%` : ""}
              {/* Drafts counted apart from the total on purpose. Backfilled
                  text is progress towards documentation, not documentation,
                  and a single percentage that hides the difference is how a
                  schema comes to look documented without being. */}
              {dft > 0 && <> · <span style={{ color: "#8A6100" }}>{num(dft)} unreviewed</span></>}
            </span>
          );
        })()}
      </div>
    </Card>

    {sel && selNode && (
      <Card style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{selNode.name}</span>
          <Chip color={selNode.kind === "view" ? VIA.view.color : B.cyanDark}>
            {selNode.kind}
          </Chip>
          <span style={{ fontSize: 12, color: INK_MUTED }}>
            {selNode.columns.length} columns
            {selNode.rows !== null ? ` · ${num(selNode.rows)} rows` : ""}
            {" · "}{areaOf(selNode)}
          </span>
          <span style={{ fontSize: 12, color: INK_MUTED }}>
            {selNode.described}/{selNode.columns.length} documented
          </span>
          {role.canManageUsers && (
            <Btn disabled={bulkBusy || !openKeys.size}
                 onClick={() => setOpenKeys(new Set())}>Close all boxes</Btn>
          )}
          {role.canManageUsers && (
            <Btn disabled={bulkBusy}
                 onClick={() => setOpenKeys(new Set([TKEY, ...selNode.columns.map(c => c.name)]))}>
              Open all
            </Btn>
          )}
          <Btn key="x" onClick={() => setSel(null)} >Close</Btn>
        </div>

        {/* ── THE MASTER SAVE ──────────────────────────────────────────────
            Open as many descriptions as you like, type in all of them, press
            this once. Nothing above writes to the database on its own, which
            is what stops one save from wiping the boxes still open. */}
        {role.canManageUsers && (pending.length > 0 || draftCols.length > 0) && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                        marginTop: 8, padding: "8px 10px", borderRadius: 6,
                        background: pending.length ? B.cyanLight : "transparent",
                        border: pending.length ? "none" : "1px dashed " + HAIRLINE }}>
            {pending.length > 0 && (
              <>
                <Btn variant="primary" disabled={bulkBusy}
                     onClick={() => runBulk(pending.map(({ table, column, description }) =>
                       ({ table, column, description })), "descriptions saved")}>
                  {bulkBusy ? "Saving…"
                    : `Save ${pending.length} change${pending.length === 1 ? "" : "s"}`}
                </Btn>
                <Btn disabled={bulkBusy}
                     onClick={() => { setEdits({}); setOpenKeys(new Set()); setBulkMsg(null); }}>
                  Discard
                </Btn>
                {/* Names them. A bulk action that only says "3 changes" is one
                    you have to trust rather than check. */}
                <span style={{ fontSize: 11, color: INK_MUTED }}>
                  {pending.map(x => x.label).join(", ")}
                </span>
              </>
            )}
            {draftCols.length > 0 && (
              /* Separate from Save on purpose. Confirming a draft is saving it
                 UNCHANGED, and sweeping untouched drafts into the Save button
                 would mark reviewed things nobody looked at. */
              <Btn disabled={bulkBusy}
                   onClick={() => runBulk(
                     draftCols.map(c => ({ table: selNode.name, column: c.name, description: c.description })),
                     "drafts confirmed")}>
                {bulkBusy ? "Confirming…"
                  : `Confirm ${draftCols.length} draft${draftCols.length === 1 ? "" : "s"} as-is`}
              </Btn>
            )}
          </div>
        )}

        {bulkMsg && (
          <div style={{ fontSize: 12, marginTop: 6,
                        color: bulkMsg.bad ? "#993C1D" : "#0F6E56" }}>{bulkMsg.text}</div>
        )}

        <div style={{ margin: "8px 0 4px" }}>
          <Description
            value={selNode.description} status={selNode.status}
            placeholder={role.canManageUsers
              ? "No description. Click to say what this table is for — it is the one piece of documentation a name cannot carry."
              : "No description."}
            canEdit={!!role.canManageUsers}
            open={openKeys.has(TKEY)} staged={edits[TKEY]}
            onOpen={() => openKey(TKEY)} onClose={() => closeKey(TKEY)}
            onChange={(t) => stage(TKEY, t)} onRevert={() => revert(TKEY)} />
        </div>

        <Neighbourhood centre={sel} edges={selEdges} onPick={setSel} />

        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10 }}>
          <div style={{ flex: "1 1 320px", overflowX: "auto" }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Columns</div>
            {/* Not a nowrap table any more — the description is prose and it
                is the column worth reading, so it gets the room. */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><Th>Column</Th><Th>Type</Th><Th>Description</Th></tr></thead>
              <tbody>
                {selNode.columns.map((c) => (
                  <tr key={c.name} style={{ verticalAlign: "top" }}>
                    <Td style={{ whiteSpace: "nowrap" }}>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{c.name}</span>
                      {c.pk && <> <Chip color={B.cyanDark}>PK</Chip></>}
                      {c.identity && <> <Chip color={INK_MUTED}>identity</Chip></>}
                    </Td>
                    <Td style={{ whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12, color: INK_MUTED }}>
                        {c.type}{c.nullable ? "" : " not null"}</span>
                    </Td>
                    <Td style={{ minWidth: 240 }}>
                      <Description
                        value={c.description} status={c.status}
                        placeholder={role.canManageUsers ? "—" : ""}
                        canEdit={!!role.canManageUsers}
                        open={openKeys.has(c.name)} staged={edits[c.name]}
                        onOpen={() => openKey(c.name)} onClose={() => closeKey(c.name)}
                        onChange={(t) => stage(c.name, t)} onRevert={() => revert(c.name)} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ flex: "1 1 380px", overflowX: "auto" }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
              Relationships ({selEdges.length})
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead><tr><Th>Direction</Th><Th>Via</Th><Th>Other table</Th><Th>Held by</Th></tr></thead>
              <tbody>
                {selEdges.map((e, i) => {
                  const out = e.from === sel;
                  return (
                    <tr key={i}>
                      <Td><span style={{ fontSize: 12, color: INK_MUTED }}>
                        {out ? "this → other" : "other → this"}</span></Td>
                      <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        {e.fromCol || "—"}</span></Td>
                      <Td>
                        <a href="#" onClick={(ev) => { ev.preventDefault(); setSel(out ? e.to : e.from); }}
                           style={{ color: B.cyan, textDecoration: "none" }}>
                          {out ? e.to : e.from}
                        </a>
                      </Td>
                      <Td><Chip color={VIA[e.via].color} title={VIA[e.via].note}>
                        {VIA[e.via].label}</Chip></Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    )}

    {areas.length === 0 && (
      <Card style={{ padding: 40, textAlign: "center", fontSize: 13, color: INK_MUTED }}>
        Nothing matches that search.
      </Card>
    )}

    {areas.map(([area, list]) => {
      const collapsed = open[area] === true;
      return (
        <Card key={area} style={{ marginBottom: 10, padding: 12 }}>
          <div onClick={() => setOpen((o) => ({ ...o, [area]: !collapsed }))}
               style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{area}</span>
            <span style={{ fontSize: 12, color: INK_MUTED }}>{list.length}</span>
            {(() => {
              const e = enforcementByArea.get(area);
              if (!e || !e.total) return null;
              const all = e.fk === e.total, none = e.fk === 0;
              return (
                <Chip color={all ? VIA.fk.color : none ? "#8A6100" : INK_MUTED}
                      title={`${e.fk} of ${e.total} relationships in this area are enforced by a foreign key`}>
                  {all ? "all enforced" : none ? "none enforced" : `${e.fk}/${e.total} enforced`}
                </Chip>
              );
            })()}
            <span style={{ marginLeft: "auto", fontSize: 11, color: INK_MUTED }}>
              {collapsed ? "show" : "hide"}
            </span>
          </div>
          {!collapsed && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {list.map((n) => (
                <button key={n.name} onClick={() => setSel(n.name)}
                  style={{ border: "1px solid " + (n.name === sel ? B.cyanDark : HAIRLINE),
                           background: n.name === sel ? B.cyan + "1A" : "transparent",
                           borderRadius: 6, padding: "4px 9px", cursor: "pointer",
                           fontSize: 12, color: INK_PRIMARY,
                           fontStyle: n.kind === "view" ? "italic" : "normal" }}>
                  {n.name}
                  {n.rows !== null && n.rows > 0 && (
                    <span style={{ color: INK_MUTED, fontSize: 10, marginLeft: 6 }}>
                      {num(n.rows)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>
      );
    })}
  </>;
};
